/**
 * Filters / Pre-Condition cluster — engine-validated cookbook seed recipes.
 *
 * Each entry validates against the real buildGraphFromRecipe engine.
 * Run the companion test to confirm every recipe builds with zero errors.
 *
 * Registered filter/utility nodes used here (verified against filter-schemas.ts
 * and utilities-schemas.ts registerFilterSchemas / registerUtilitySchemas):
 *
 *   REGISTERED (safe to use):
 *     priceChange      — category 'utilities'; input_1 (ohlcv); output 'value'
 *     volumeFilter     — category 'utilities'; input_1 (ohlcv); output 'value'
 *     barSize          — category 'utilities'; input_1 (ohlcv); output 'value'
 *     barOffset        — category 'utilities'; input_1 (number|ohlcv); output 'output'
 *     barsConditionCounter — category 'utilities'; inputs input_1/input_2; output 'value'
 *     rollingRange     — category 'utilities'; input_1; output 'range'
 *     simplemovingaverage — category 'indicators'; input_1; output 'sma'
 *     condition        — category 'logic'; outputs 'true'/'false'
 *     merge            — category 'logic'; output 'result'
 *     timeout          — category 'logic'; input 'signal' (boolean); output 'output'
 *
 *   DEFINED BUT NOT REGISTERED (do not use in recipes):
 *     sessionVolume, customPeriodVolume, customPeriodPercentChange,
 *     percentChangeFromClose/Open/High/Low, dailyPercentChange,
 *     sessionPercentChange, weeklyPercentChange, monthlyPercentChange,
 *     yearlyPercentChange, positionInRange (is registered but not used here),
 *     cooldown (appears in types/constants but has no schema)
 */

import type { CookbookEntry } from './types'

// ── 5. First Red Candle After Momentum Run ────────────────────
//
// Pattern: detect the first bearish (red) candle that appears after a big
// intraday session run. Useful as a short trigger or a profit-take signal
// after a parabolic move. We combine:
//   (a) session % gain has been large (priceChange session > +5%)
//   (b) current bar is a red candle (close < open)
// When both are true simultaneously, the signal fires.
//
// Note: 'cooldown' has no registered schema — we use the 'timeout' (Signal Hold)
// node to hold the entry signal for 1 bar so the pattern is acknowledged at the
// next bar open rather than at the bar's close.
//
// Nodes: priceData → priceChange(session) → condition(chg > 5%)
//                 → condition(close < open)
//                 → merge(AND) → timeout(1) → Trade Manager
const firstOppositeCandleEntry: CookbookEntry = {
  key: 'first-opposite-candle-entry',
  name: 'First Red Candle After Momentum Run',
  category: 'filters',
  intent: 'Short signal: session gain > 5% AND current bar closes red (close < open)',
  summary:
    'Triggers a short entry on the first red 1-minute candle after a session gain exceeding 5%.',
  difficulty: 'advanced',
  tags: ['Reversal'],
  whenToUse:
    'Use on intraday parabolic-short setups where a stock has already made an extreme session move and you are waiting for the first crack in buying. The first red candle is one of the three classic entry triggers for fading an overextended stock — cleaner than picking a top, safer than chasing the opening-range-low break on a still-vertical tape.',
  whatToLookFor:
    'A 1-minute candle that closes below its own open after a sustained session run of 5%+. The most reliable setups show this candle rolling over near VWAP or at a visible intraday resistance level; a random red candle mid-run with no context is less reliable and may just be a brief pullback before continuation.',
  howItWorks:
    "priceData (1m timeframe) feeds two independent condition checks. priceChange(session) computes the intraday gain from the session open; a condition verifies it exceeds 5%, confirming the stock has made a parabolic run. A second condition directly compares price.close < price.open on the current bar, identifying the red candle. merge(AND) requires both conditions simultaneously — a red candle only counts after the big run is already established. The signal then passes through a timeout(period:1) node that holds it for one bar so the entry executes at the next bar's open rather than at the close of the signal bar, which is standard practice for first-red-candle short entries. the Trade Manager is set to Short.",
  filterSide: false,
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '1m' },
    },
    // Session % gain from the open using the registered priceChange node
    {
      id: 'session_chg',
      type: 'priceChange',
      params: { timeframe: 'session' },
      inputs: { input_1: 'price.close' },
    },
    // Big run: session gain exceeds +5%
    {
      id: 'big_run',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'session_chg.value', input_2: 5 },
    },
    // Red candle: close below open on the current bar
    {
      id: 'red_candle',
      type: 'condition',
      params: { operator: '<' },
      inputs: { input_1: 'price.close', input_2: 'price.open' },
    },
    // Both must be true: big run AND current bar is red
    {
      id: 'signal_gate',
      type: 'merge',
      params: { operator: 'AND' },
      inputs: { input_1: 'big_run.true', input_2: 'red_candle.true' },
    },
    // Hold signal for 1 bar (acts as a minimal cool-down acknowledging the pattern)
    {
      id: 'hold',
      type: 'timeout',
      params: { period: 1 },
      inputs: { signal: 'signal_gate.result' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Short' },
    feeds: { socket: 'trigger', from: 'hold.output' },
  },
}

// ── Export ───────────────────────────────────────────────────

export const filtersSeedRecipes: CookbookEntry[] = [firstOppositeCandleEntry]
